import { useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { formatAmount } from '@shared/lib/format';
import type { Receipt } from '@features/receipts';

const CATEGORY_COLORS = ['#059669', '#0ea5e9', '#8b5cf6', '#f59e0b', '#64748b', '#ec4899'];

interface CategorySlice {
  name: string;
  value: number;
  percent: number;
}

function aggregateByCategory(receipts: Receipt[]): CategorySlice[] {
  const totals = new Map<string, number>();
  for (const r of receipts) {
    totals.set(r.category, (totals.get(r.category) ?? 0) + r.total);
  }
  const grandTotal = [...totals.values()].reduce((sum, value) => sum + value, 0);
  if (grandTotal === 0) return [];

  return [...totals.entries()]
    .map(([name, value]) => ({
      name,
      value: Math.round(value * 100) / 100,
      percent: Math.round((value / grandTotal) * 100),
    }))
    .sort((a, b) => b.value - a.value);
}

function DonutTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name: string; payload: CategorySlice; fill?: string }>;
}) {
  if (!active || !payload?.length) return null;
  const slice = payload[0];
  return (
    <div
      style={{ backgroundColor: '#ffffff' }}
      className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-lg shadow-slate-900/5"
    >
      <div className="flex items-center gap-1.5">
        <span className="size-2 rounded-full" style={{ backgroundColor: slice.fill }} />
        <p className="text-xs font-medium text-slate-500">{slice.name}</p>
      </div>
      <p className="mt-0.5 text-sm font-semibold text-slate-900">
        {formatAmount(slice.payload.value, 'EUR')}{' '}
        <span className="font-normal text-slate-400">· {slice.payload.percent} %</span>
      </p>
    </div>
  );
}

/**
 * « Spending by Category » — donut chart avec total au centre et
 * segment agrandi au survol.
 */
export function CategoryDonut({ receipts }: { receipts: Receipt[] }) {
  const data = aggregateByCategory(receipts);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  if (data.length === 0) {
    return (
      <div className="flex h-56 items-center justify-center text-sm text-slate-400">
        Aucune dépense enregistrée.
      </div>
    );
  }

  const total = data.reduce((sum, slice) => sum + slice.value, 0);

  return (
    <div className="flex items-center gap-6">
      <div
        className="relative h-48 w-48 shrink-0"
        role="img"
        aria-label="Répartition des dépenses par catégorie"
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip
              content={<DonutTooltip />}
              wrapperStyle={{ zIndex: 50, outline: 'none' }}
              isAnimationActive={false}
            />
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius={56}
              outerRadius={84}
              paddingAngle={3}
              cornerRadius={4}
              strokeWidth={0}
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {data.map((slice, index) => (
                <Cell
                  key={slice.name}
                  fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                  opacity={activeIndex === null || activeIndex === index ? 1 : 0.35}
                  className="transition-opacity duration-150"
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {/* Total au centre */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-[10px] font-medium tracking-wide text-slate-400 uppercase">
            Total
          </span>
          <span className="text-base font-semibold tracking-tight text-slate-900">
            {formatAmount(total, 'EUR')}
          </span>
        </div>
      </div>

      {/* Légende */}
      <ul className="flex min-w-0 flex-1 flex-col gap-2.5">
        {data.map((slice, index) => (
          <li
            key={slice.name}
            onMouseEnter={() => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
            className="flex cursor-default items-center gap-2.5 rounded-md px-1 py-0.5 transition-colors hover:bg-slate-50"
          >
            <span
              className="size-2.5 shrink-0 rounded-full transition-opacity duration-150"
              style={{
                backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
                opacity: activeIndex === null || activeIndex === index ? 1 : 0.35,
              }}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate text-sm text-slate-600">{slice.name}</span>
            <span className="shrink-0 text-sm font-semibold text-slate-900">{slice.percent} %</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
